/**
 * Section "API-Tokens" of the settings page "Mein Profil": the logged-in user's personal API tokens.
 * A tool (e.g. an MCP server) sends one as `Authorization: Bearer <token>` and acts as the user.
 *
 * Lists the own tokens (`GET /api/v1/me/api-tokens`: name, prefix, dates, status — never the token),
 * creates one (`createOwnApiToken`: name, validity 30/90/365 days with 90 preselected, the current
 * password with the brake; the server generates the token) and revokes one after a confirmation
 * (`revokeOwnApiToken`, the action command `revoke.api-token`). The list is reloaded after each, a
 * refusal included, so a token revoked in another tab no longer shows as active. Creating and revoking
 * keep separate error texts, each next to what it belongs to.
 *
 * The created token is shown exactly once. It lives in one `@state` string only — not bound to a
 * form, not stored, not logged — and is cleared when it is hidden, on the next create, when the
 * element is removed and when it is attached again. An answer that arrives after the element was
 * removed is dropped. Copying uses the clipboard, else select and copy, else asks to copy by hand
 * (`copyApiToken`; the clipboard is missing on plain http).
 *
 * Rendered by `ha-own-profile-view`, which passes the own user uid. Extends `AbstractO2dElement`
 * (Light DOM, ADR-007); styles in ralphy.css (`own-api-tokens__*`).
 */

import { AbstractO2dElement } from '@o2d/shared';
import { get } from 'lit-translate';
import { html } from 'lit';
import type { TemplateResult } from 'lit';
import { customElement } from 'lit/decorators/custom-element.js';
import { property } from 'lit/decorators/property.js';
import { state } from 'lit/decorators/state.js';
import { container } from 'tsyringe';
import { bindTo } from '@ha/formhandler';
import type { ComplexFormControl, PrimitiveFormControl } from '@ha/formhandler';
import { DateTime } from 'luxon';
import { Subject, takeUntil } from 'rxjs';
import '../_inputs/ha-input-text/ha-input-text.component';
import '../_inputs/ha-input-radio-group/ha-input-radio-group.component';
import type { RadioButtonItem } from '../_inputs/ha-input-checkbox/radio-button-item.interface';
import { BackendClient } from '../../connectivity/backend-client';
import { CommandBus } from '../../services/command-bus.service';
import { createOwnApiTokenForm, isOwnApiTokenFormSavable, ownApiTokenNameTooLong } from '../_forms/own-api-token-form';
import type { OwnApiTokenFormData } from '../_forms/own-api-token-form';
import { createOwnApiToken } from '../../helpers/own-api-token-save';
import { revokeOwnApiToken } from '../../helpers/own-api-token-revoke';
import { copyApiToken } from '../../helpers/own-api-token-copy';
import { apiTokenErrorKey, OWN_API_TOKEN_MAX_ACTIVE, OWN_API_TOKEN_VALIDITY_DAYS } from '../../helpers/own-api-token.state';
import type { OwnApiToken, OwnApiTokenErrorKey } from '../../helpers/own-api-token.state';
import { DateHelper } from '../../helpers/date.helper';
import { PASSWORD_LOCK_MINUTES } from '../../helpers/password-rule';
import { UiHelper } from '../../helpers/ui.helper';
import { NOTIFICATION_DURATION } from '../../constants';

@customElement('ha-own-api-tokens')
export class HaOwnApiTokensComponent extends AbstractO2dElement {

    /** The logged-in user's own uid (transport form); `revoke.api-token` names it. */
    @property({ type: String }) public userUid = '';

    @state() private _tokens: OwnApiToken[] | null = null;

    @state() private _loadFailed = false;

    @state() private _form: ComplexFormControl<OwnApiTokenFormData, unknown> | null = null;

    @state() private _creating = false;

    @state() private _revoking = false;

    /** Why the last create was refused, shown under the create form. */
    @state() private _error: OwnApiTokenErrorKey | null = null;

    /** Why the last revocation was refused, shown under the list. */
    @state() private _revokeError: OwnApiTokenErrorKey | null = null;

    /** The token just created — shown once, never stored anywhere else. */
    @state() private _createdToken: string | null = null;

    @state() private _copyResult: 'clipboard' | 'selected' | 'manual' | null = null;

    private readonly _formTeardown$ = new Subject<void>();

    /** Counts list loads, so an older answer never replaces a newer one. */
    private _loadSequence = 0;

    /** The validity choices, built once on first render (the texts are loaded by then). */
    private _validityItems: RadioButtonItem<number>[] | null = null;

    /** Starts with an empty form, no shown token and no earlier refusal, and loads the list. */
    public connectedCallback(): void {
        super.connectedCallback();
        this._createdToken = null;
        this._copyResult = null;
        this._error = null;
        this._revokeError = null;
        this._resetForm();
        void this._loadTokens();
    }

    /** Forgets the shown token and drops the form, so nothing of either outlives the element on the page. */
    public disconnectedCallback(): void {
        this._createdToken = null;
        this._copyResult = null;
        this._formTeardown$.next();
        this._form?.dispose();
        this._form = null;
        super.disconnectedCallback();
    }

    /** Loads the own tokens; an answer overtaken by a later load is dropped. */
    private async _loadTokens(): Promise<void> {
        const sequence = ++this._loadSequence;
        try {
            const tokens = (await container.resolve(BackendClient).get<OwnApiToken[]>('/api/v1/me/api-tokens')) ?? [];
            if (sequence === this._loadSequence) {
                this._tokens = tokens;
                this._loadFailed = false;
            }
        } catch (error) {
            console.error(error);
            if (sequence === this._loadSequence) {
                this._loadFailed = true;
            }
        }
    }

    /** A fresh, empty create form (90 days preselected); every change re-renders and clears an earlier refusal text. */
    private _resetForm(): void {
        this._formTeardown$.next();
        const previous = this._form;
        const form = createOwnApiTokenForm();
        form.event$.pipe(takeUntil(this._formTeardown$)).subscribe(notification => {
            if (notification.event === 'value.changed') {
                this._error = null;
            }
            this.requestUpdate();
        });
        this._form = form;
        previous?.dispose();
    }

    /** How many of the listed tokens are active (0 until the list is loaded; the server enforces the limit). */
    private _activeCount(): number {
        return (this._tokens ?? []).filter(token => token.status === 'active').length;
    }

    /** The validity choices for the radio group. */
    private _validities(): RadioButtonItem<number>[] {
        this._validityItems ??= OWN_API_TOKEN_VALIDITY_DAYS.map(days => ({
            id: `days-${days}`,
            value: days,
            label: get('ownApiToken.create.days', { days: String(days) }),
        }));

        return this._validityItems;
    }

    /**
     * Sends the create form through `createOwnApiToken`. On success shows the token once and moves the
     * focus to "Kopieren"; on a refusal clears the typed password and shows why. An answer that arrives
     * after the element was removed is dropped, so a token never lands in a detached element.
     */
    private readonly _onCreate = async (): Promise<void> => {
        const form = this._form;
        if (form === null || this._creating || !isOwnApiTokenFormSavable(form) || this._activeCount() >= OWN_API_TOKEN_MAX_ACTIVE) {
            return;
        }

        this._creating = true;
        this._error = null;
        this._createdToken = null;
        this._copyResult = null;
        try {
            const outcome = await createOwnApiToken(form.getValue()!, container.resolve(BackendClient));
            // Removed meanwhile — or removed and attached again, which builds a new form: the answer
            // belongs to neither and is dropped (a token created this way is listed and can be revoked).
            if (!this.isConnected || this._form !== form) {
                return;
            }
            if (outcome.kind === 'refused') {
                // Clearing the password emits value.changed, which clears `_error` — so it is set AFTER.
                (form.getField('currentPassword') as PrimitiveFormControl<string, unknown>).value = '';
                this._error = apiTokenErrorKey(outcome.error);
                if (this._error === 'limitReached') {
                    void this._loadTokens();
                }
                return;
            }

            this._createdToken = outcome.created.token;
            UiHelper.notify({
                closable: true,
                level: 'info',
                title: get('ownApiToken.created.successTitle'),
                content: html`<p>${get('ownApiToken.created.successMessage', { name: outcome.created.name })}</p>`,
                duration: NOTIFICATION_DURATION,
            });
            this._resetForm();
            void this._loadTokens();
            await this.updateComplete;
            this.querySelector<HTMLButtonElement>('.own-api-tokens__copy')?.focus();
        } finally {
            this._creating = false;
        }
    };

    /** Hides the shown token for good. */
    private readonly _onHide = (): void => {
        this._createdToken = null;
        this._copyResult = null;
    };

    /** Copies the shown token: clipboard, else select and copy, else a hint to copy by hand. */
    private readonly _onCopy = async (): Promise<void> => {
        const token = this._createdToken;
        if (token === null) {
            return;
        }
        const field = this.querySelector<HTMLInputElement>('.own-api-tokens__created-value');
        // Empty the live region first, so a second "Kopiert." is a change that is announced again.
        this._copyResult = null;
        await this.updateComplete;
        this._copyResult = await copyApiToken(token, {
            clipboard: navigator.clipboard,
            selectAndCopy: () => {
                if (!field) {
                    return false;
                }
                field.select();

                // The legacy copy command is deprecated, and kept deliberately: it is the only copy that
                // works where navigator.clipboard is missing (plain http, measured on the local stack).
                return document.execCommand('copy');
            },
        });
    };

    /** Asks for confirmation, then revokes the token through `revokeOwnApiToken` and reloads the list either way. */
    private async _onRevoke(token: OwnApiToken): Promise<void> {
        if (this._revoking) {
            return;
        }
        this._revoking = true;
        this._revokeError = null;
        this._error = null;
        try {
            const choice = await UiHelper.choose({
                title: get('ownApiToken.revoke.title'),
                content: html`<p>${get('ownApiToken.revoke.question', { name: token.name })}</p>`,
                confirmLabel: get('ownApiToken.revoke.confirm'),
                cancelLabel: get('ownApiToken.revoke.cancel'),
            });
            if (choice !== 'confirm') {
                return;
            }

            const outcome = await revokeOwnApiToken(this.userUid, token.uid, container.resolve(CommandBus));
            if (!this.isConnected) {
                return;
            }
            if (outcome.kind === 'refused') {
                this._revokeError = apiTokenErrorKey(outcome.error);
            } else {
                UiHelper.notify({
                    closable: true,
                    level: 'info',
                    title: get('ownApiToken.revoke.successTitle'),
                    content: html`<p>${token.name}</p>`,
                    duration: NOTIFICATION_DURATION,
                });
            }
            await this._loadTokens();
        } finally {
            this._revoking = false;
        }
    }

    /** A date of the list in the house format, or "nie" for a token never used. */
    private _formatDate(iso: string | null): string {
        return iso === null ? get('ownApiToken.list.never') : DateHelper.fromDateTimeToDateTimeString(DateTime.fromISO(iso));
    }

    /** The text of a refusal; the brake and the limit carry their numbers. */
    private _errorText(key: OwnApiTokenErrorKey): string {
        return get(`ownApiToken.error.${key}`, { minutes: String(PASSWORD_LOCK_MINUTES), max: String(OWN_API_TOKEN_MAX_ACTIVE) });
    }

    /** The list of the own tokens, its load error, or the empty hint; a revoke refusal sits right below. */
    private _renderList(): TemplateResult {
        const revokeError = this._revokeError !== null
            ? html`<p class="own-api-tokens__error" data-type="api-token-revoke-error" role="alert">${this._errorText(this._revokeError)}</p>`
            : html``;
        if (this._loadFailed) {
            return html`<p class="own-api-tokens__error" data-type="api-token-load-failed" role="alert">${get('ownApiToken.list.loadFailed')}</p>${revokeError}`;
        }
        const tokens = this._tokens;
        if (tokens === null) {
            return html``;
        }
        if (tokens.length === 0) {
            return html`<p class="own-profile__hint own-api-tokens__empty">${get('ownApiToken.list.empty')}</p>${revokeError}`;
        }

        return html`
            <table class="own-api-tokens__list">
                <thead><tr>
                    <th>${get('ownApiToken.list.name')}</th>
                    <th>${get('ownApiToken.list.prefix')}</th>
                    <th>${get('ownApiToken.list.createdAt')}</th>
                    <th>${get('ownApiToken.list.expiresAt')}</th>
                    <th>${get('ownApiToken.list.lastUsedAt')}</th>
                    <th>${get('ownApiToken.list.statusHeader')}</th>
                    <th><span class="own-api-tokens__visually-hidden">${get('ownApiToken.list.revoke')}</span></th>
                </tr></thead>
                <tbody>
                    ${tokens.map(token => html`
                        <tr class="own-api-tokens__row" data-status=${token.status} data-prefix=${token.prefix}>
                            <td>${token.name}</td>
                            <td><code>${token.prefix}…</code></td>
                            <td class="own-api-tokens__date">${this._formatDate(token.createdAt)}</td>
                            <td class="own-api-tokens__date">${this._formatDate(token.expiresAt)}</td>
                            <td class="own-api-tokens__date">${this._formatDate(token.lastUsedAt)}</td>
                            <td>${get(`ownApiToken.list.status.${token.status}`)}</td>
                            <td>${token.revokedAt === null
                                ? html`<button class="own-api-tokens__revoke delete buttonform-marker" type="button" ?disabled=${this._revoking}
                                        data-label=${get('ownApiToken.list.revoke')}
                                        aria-label=${get('ownApiToken.list.revokeNamed', { name: token.name })}
                                        @click=${() => this._onRevoke(token)}></button>`
                                : html``}</td>
                        </tr>
                    `)}
                </tbody>
            </table>
            ${revokeError}
        `;
    }

    /** The token just created, with "Kopieren" and "Ausblenden". */
    private _renderCreated(token: string): TemplateResult {
        return html`
            <div class="own-api-tokens__created" data-type="api-token-created">
                <h4 class="own-api-tokens__created-title" id="own-api-tokens-created-title">${get('ownApiToken.created.title')}</h4>
                <p class="own-profile__hint">${get('ownApiToken.created.hint')}</p>
                <input class="own-api-tokens__created-value" type="text" readonly autocomplete="off" spellcheck="false"
                    aria-labelledby="own-api-tokens-created-title" .value=${token}>
                <button class="own-api-tokens__copy" type="button" @click=${this._onCopy}>${get('ownApiToken.created.copy')}</button>
                <button class="own-api-tokens__hide" type="button" @click=${this._onHide}>${get('ownApiToken.created.hide')}</button>
            </div>
        `;
    }

    /** The create form: name, validity, current password, its hints and refusal, and the button. */
    private _renderCreateForm(form: ComplexFormControl<OwnApiTokenFormData, unknown>): TemplateResult {
        const atLimit = this._activeCount() >= OWN_API_TOKEN_MAX_ACTIVE;

        return html`
            <h4 class="own-api-tokens__create-title">${get('ownApiToken.create.title')}</h4>
            <div class="own-profile__form own-api-tokens__form">
                <ha-input-text class="own-api-tokens__name max9999-flexrow max355-flexcolumn" data-type="api-token-name"
                    label=${get('ownApiToken.create.name')} ${bindTo(form, 'name')}></ha-input-text>
                <ha-input-radio-group class="own-api-tokens__validity max9999-flexrow max355-flexcolumn" data-type="api-token-validity"
                    label=${get('ownApiToken.create.validity')} .items=${this._validities()} ${bindTo(form, 'expiresInDays')}></ha-input-radio-group>
                <ha-input-text class="own-api-tokens__password max9999-flexrow max355-flexcolumn" data-type="api-token-password"
                    label=${get('ownApiToken.create.currentPassword')} obscure ${bindTo(form, 'currentPassword')}></ha-input-text>
            </div>
            ${ownApiTokenNameTooLong(form) ? html`<p class="own-api-tokens__rule" data-type="api-token-name-too-long">${this._errorText('invalidName')}</p>` : html``}
            ${atLimit ? html`<p class="own-profile__hint own-api-tokens__limit">${get('ownApiToken.create.limitReached', { max: String(OWN_API_TOKEN_MAX_ACTIVE) })}</p>` : html``}
            ${this._error !== null
                ? html`<p class="own-api-tokens__error" data-type="api-token-error" role="alert">${this._errorText(this._error)}</p>`
                : html``}
            <button class="own-api-tokens__create" type="button"
                ?disabled=${this._creating || atLimit || !isOwnApiTokenFormSavable(form)}
                @click=${this._onCreate}>
                ${get('ownApiToken.create.button')}
            </button>
        `;
    }

    /** The section: title, hint, list, the shown token, a live region for the copy result, and the create form. */
    protected render(): TemplateResult {
        const copyText = this._copyResult === null ? ''
            : get(this._copyResult === 'manual' ? 'ownApiToken.created.copyManually' : 'ownApiToken.created.copied');

        return html`
            <h3 class="own-profile__title own-api-tokens__title">${get('ownApiToken.title')}</h3>
            <p class="own-profile__hint">${get('ownApiToken.hint')}</p>
            ${this._renderList()}
            ${this._createdToken !== null ? this._renderCreated(this._createdToken) : html``}
            <p class="own-api-tokens__copy-result" role="status">${copyText}</p>
            ${this._form !== null ? this._renderCreateForm(this._form) : html``}
        `;
    }
}
